import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Dumbbell, Loader2 } from "lucide-react";
import { fetchAPI, formatDate } from "../lib/utils";
import { useLanguage } from "../contexts/LanguageContext";
import {
	EXERCISE_TYPES,
	formatExerciseDisplayName,
	formatExercisePrescription,
} from "../lib/programBuilderUtils";
import { exerciseTypeIcon } from "./programBuilder/exerciseIcons";
import { ExerciseDetailAccordion } from "../pages/workouts/components/ExerciseDetailAccordion";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";

/**
 * La seduta dietro una voce di storico del profilo prestativo.
 *
 * Lo storico dice *quanto* (110kg, 3:42), non *dove*: senza la giornata da cui
 * arriva, il numero non si interpreta. Qui si legge la prescrizione completa e
 * i risultati di tutta la seduta, con la stessa resa del diario
 * (`ExerciseDetailAccordion`) — non una seconda lettura degli stessi dati.
 *
 * Il payload e' pesante, quindi NON viaggia con lo storico: si carica alla
 * prima apertura e solo per la voce aperta.
 *
 * @param {string} resultId - id del risultato della voce di storico
 * @param {string|null} athleteId - valorizzato = vista coach su un atleta
 */
export function SessionDetailDialog({
	open,
	onOpenChange,
	resultId,
	athleteId = null,
	exerciseName = "",
}) {
	const { t, language } = useLanguage();
	const [detail, setDetail] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [expanded, setExpanded] = useState({});

	useEffect(() => {
		if (!open || !resultId) return;

		let isActive = true;
		const load = async () => {
			setLoading(true);
			setError("");
			try {
				const base = athleteId
					? `/coach/athletes/${encodeURIComponent(athleteId)}/performance-profile/history/session`
					: "/profile/performance-profile/history/session";
				const data = await fetchAPI(
					`${base}/${encodeURIComponent(resultId)}`,
				);
				if (!isActive) return;
				setDetail(data || null);
				// Aperto sull'esercizio che ha dato il valore al profilo: e' la
				// domanda con cui si arriva qui, il resto della seduta e' contorno.
				const focusIndex = (data?.exercises || []).findIndex(
					(item) => item?.is_focus,
				);
				setExpanded(focusIndex >= 0 ? { [focusIndex]: true } : { 0: true });
			} catch (loadError) {
				if (!isActive) return;
				setDetail(null);
				setError(loadError?.message || t("sessionDetailLoadError"));
			} finally {
				if (isActive) setLoading(false);
			}
		};

		load();
		return () => {
			isActive = false;
		};
	}, [open, resultId, athleteId, t]);

	const handleOpenChange = useCallback(
		(next) => {
			onOpenChange(next);
			if (!next) {
				setDetail(null);
				setError("");
				setExpanded({});
			}
		},
		[onOpenChange],
	);

	const day = detail?.day;
	const program = detail?.program;
	const week = detail?.week;
	const dayDate = day?.date || detail?.date;

	const contextLine = [
		program?.title,
		week?.label ||
			(week?.week_number ? `${t("week")} ${week.week_number}` : ""),
		day?.title ||
			(day?.day_number ? `${t("day")} ${day.day_number}` : ""),
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent className="bg-card border-border w-[calc(100vw-1rem)] sm:w-[calc(100vw-2rem)] max-w-2xl p-3 sm:p-6 max-h-[92svh] overflow-hidden flex flex-col">
				<DialogDescription className="hidden" />
				<DialogHeader className="space-y-1.5 pr-7">
					<DialogTitle className="font-heading flex items-center gap-2">
						<Dumbbell className="w-4 h-4 text-volt" />
						{t("sessionDetailTitle")}
					</DialogTitle>
					{dayDate && (
						<p className="text-xs text-volt">{formatDate(dayDate, language)}</p>
					)}
					{contextLine && (
						<p className="text-xs text-muted-foreground break-words">
							{contextLine}
						</p>
					)}
					{!contextLine && exerciseName && (
						<p className="text-xs text-muted-foreground break-words">
							{exerciseName}
						</p>
					)}
				</DialogHeader>

				{loading ? (
					<div className="flex justify-center items-center gap-2 py-8 text-sm text-muted-foreground">
						<Loader2 className="w-4 h-4 animate-spin" />
						{t("loading")}
					</div>
				) : error ? (
					<p className="text-sm text-red-400">{error}</p>
				) : detail ? (
					<div className="space-y-2 overflow-y-auto pr-1 -mr-1">
						{day?.warmup && (
							<DayNote label={t("warmup")} value={day.warmup} />
						)}

						{(detail.exercises || []).map((exercise, index) => (
							<SessionExerciseRow
								key={`${exercise?.exercise_id || "ex"}-${index}`}
								exercise={exercise}
								isOpen={Boolean(expanded[index])}
								onToggle={() =>
									setExpanded((prev) => ({ ...prev, [index]: !prev[index] }))
								}
								isCoachView={Boolean(athleteId)}
								language={language}
								t={t}
							/>
						))}

						{day?.cooldown && (
							<DayNote label={t("cooldown")} value={day.cooldown} />
						)}
						{day?.notes && <DayNote label={t("notes")} value={day.notes} />}

						{(detail.extra_results || []).length > 0 && (
							<div className="space-y-2 pt-1">
								<p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
									{t("sessionDetailExtraResults")}
								</p>
								{detail.extra_results.map((result, index) => (
									<SessionExerciseRow
										key={`extra-${result?.result_id || index}`}
										exercise={{
											exercise_id: result?.exercise_id,
											exercise_name: result?.exercise_name,
											exercise_type: result?.exercise_type || "strength",
											conditioning_type: result?.conditioning_type,
											prescription_missing: true,
											result,
										}}
										isOpen={Boolean(expanded[`extra-${index}`])}
										onToggle={() =>
											setExpanded((prev) => ({
												...prev,
												[`extra-${index}`]: !prev[`extra-${index}`],
											}))
										}
										isCoachView={Boolean(athleteId)}
										language={language}
										t={t}
									/>
								))}
							</div>
						)}
					</div>
				) : null}
			</DialogContent>
		</Dialog>
	);
}

function DayNote({ label, value }) {
	return (
		<div className="rounded-sm bg-secondary/40 px-3 py-2">
			<p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
				{label}
			</p>
			<p className="text-xs text-foreground whitespace-pre-wrap">{value}</p>
		</div>
	);
}

function SessionExerciseRow({
	exercise,
	isOpen,
	onToggle,
	isCoachView,
	language,
	t,
}) {
	const exerciseType = exercise?.exercise_type || "strength";
	const TypeIcon = exerciseTypeIcon(exerciseType);
	const typeColor =
		EXERCISE_TYPES.find((item) => item.value === exerciseType)?.color ||
		"text-volt";
	const name = formatExerciseDisplayName(exercise);
	const prescription = formatExercisePrescription(exercise, t);
	const hasResult = Boolean(exercise?.result);
	// Lo stesso esercizio puo' essere stato registrato piu' volte nella
	// giornata: ogni risultato tiene la sua prescrizione, non se ne perde uno.
	const extraResults = exercise?.extra_results || [];

	return (
		<div
			className={`rounded-sm border overflow-hidden ${
				exercise?.is_focus
					? "border-volt/50 bg-volt/[0.04]"
					: "border-border/50 bg-secondary/30"
			}`}
		>
			<button
				type="button"
				onClick={onToggle}
				aria-expanded={isOpen}
				className="w-full flex items-center gap-2 px-3 py-2.5 text-left min-h-11 hover:bg-secondary/40"
			>
				{isOpen ? (
					<ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
				) : (
					<ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
				)}
				<TypeIcon className={`w-3.5 h-3.5 shrink-0 ${typeColor}`} />
				<span className="flex-1 min-w-0">
					<span className="block text-sm text-foreground truncate">{name}</span>
					{prescription && (
						<span className="block text-[11px] text-muted-foreground truncate">
							{prescription}
						</span>
					)}
				</span>
				{exercise?.session_label && (
					<span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-sm bg-secondary text-muted-foreground">
						{exercise.session_label}
					</span>
				)}
				{exercise?.is_focus && (
					<span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-sm bg-volt/15 text-volt">
						{t("sessionDetailProfileValue")}
					</span>
				)}
			</button>

			{isOpen && (
				<div className="border-t border-border/40">
					{exercise?.prescription_missing && (
						<p className="px-3 pt-2 text-[11px] text-muted-foreground">
							{t("sessionDetailNoPrescription")}
						</p>
					)}
					<ExerciseDetailAccordion
						exercise={exercise}
						language={language}
						isCoachView={isCoachView}
					/>
					{extraResults.map((result, index) => (
						<ExerciseDetailAccordion
							key={`extra-result-${result?.result_id || index}`}
							exercise={{ ...exercise, result }}
							language={language}
							isCoachView={isCoachView}
						/>
					))}
					{!hasResult && !exercise?.prescription_missing && (
						<p className="px-3 pb-3 text-[11px] text-muted-foreground">
							{t("sessionDetailNoResult")}
						</p>
					)}
				</div>
			)}
		</div>
	);
}
